import React, { useState, useRef, useEffect, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReceiptEditor = ({ imageData, onSave, onCancel, initialFilename = '' }) => {
  const canvasRef = useRef(null);
  const originalCanvasRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [originalImage, setOriginalImage] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [activeTab, setActiveTab] = useState('enhance');
  const [saving, setSaving] = useState(false);
  
  // Enhancement settings
  const [settings, setSettings] = useState({
    brightness: 0,
    contrast: 0,
    saturation: 0,
    sharpness: 0,
    exposure: 0,
    highlights: 0,
    shadows: 0,
    temperature: 0,
    rotation: 0,
    flipH: false,
    flipV: false
  });

  // Preset modes (like Office Lens)
  const presets = [
    { id: 'original', name: 'Original', icon: '📷', settings: { brightness: 0, contrast: 0, saturation: 0, sharpness: 0, exposure: 0 } },
    { id: 'document', name: 'Document', icon: '📄', settings: { brightness: 15, contrast: 30, saturation: -50, sharpness: 40, exposure: 10 } },
    { id: 'receipt', name: 'Receipt', icon: '🧾', settings: { brightness: 20, contrast: 40, saturation: -100, sharpness: 50, exposure: 15 } },
    { id: 'whiteboard', name: 'Whiteboard', icon: '📋', settings: { brightness: 30, contrast: 50, saturation: -100, sharpness: 30, exposure: 20 } },
    { id: 'photo', name: 'Photo', icon: '🖼️', settings: { brightness: 5, contrast: 10, saturation: 10, sharpness: 20, exposure: 5 } },
    { id: 'vivid', name: 'Vivid', icon: '🌈', settings: { brightness: 10, contrast: 20, saturation: 30, sharpness: 25, exposure: 10 } },
    { id: 'bw', name: 'B&W', icon: '⬛', settings: { brightness: 10, contrast: 25, saturation: -100, sharpness: 30, exposure: 5 } },
    { id: 'high_contrast', name: 'High Contrast', icon: '◐', settings: { brightness: 0, contrast: 60, saturation: 0, sharpness: 40, exposure: 0 } }
  ];

  const [activePreset, setActivePreset] = useState('original');

  // Load image on mount
  useEffect(() => {
    loadImage();
  }, [imageData]);

  const loadImage = async () => {
    setIsLoading(true);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      setOriginalImage(img);
      setIsLoading(false);
      applyFilters(img);
    };
    
    img.onerror = () => {
      console.error('Failed to load image');
      setIsLoading(false);
    };

    // Handle different image data formats
    if (typeof imageData === 'string') {
      if (imageData.startsWith('data:') || imageData.startsWith('http')) {
        img.src = imageData;
      } else {
        img.src = `${BACKEND_URL}${imageData}`;
      }
    } else if (imageData instanceof Blob) {
      img.src = URL.createObjectURL(imageData);
    }
  };

  // Apply filters to canvas
  const applyFilters = useCallback((img = originalImage) => {
    if (!img || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    let width = img.width;
    let height = img.height;
    
    // Handle rotation
    if (settings.rotation === 90 || settings.rotation === 270) {
      canvas.width = height;
      canvas.height = width;
    } else {
      canvas.width = width;
      canvas.height = height;
    }

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Save context state
    ctx.save();
    
    // Apply transformations
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((settings.rotation * Math.PI) / 180);
    if (settings.flipH) ctx.scale(-1, 1);
    if (settings.flipV) ctx.scale(1, -1);
    
    // Draw image centered
    if (settings.rotation === 90 || settings.rotation === 270) {
      ctx.drawImage(img, -height / 2, -width / 2, height, width);
    } else {
      ctx.drawImage(img, -width / 2, -height / 2, width, height);
    }
    
    ctx.restore();

    // Get image data for pixel manipulation
    const imageDataObj = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageDataObj.data;

    // Apply adjustments
    for (let i = 0; i < data.length; i += 4) {
      let r = data[i];
      let g = data[i + 1];
      let b = data[i + 2];

      // Brightness (-100 to 100)
      const brightness = settings.brightness * 2.55;
      r += brightness;
      g += brightness;
      b += brightness;

      // Contrast (-100 to 100)
      const contrast = (settings.contrast / 100) + 1;
      r = ((r - 128) * contrast) + 128;
      g = ((g - 128) * contrast) + 128;
      b = ((b - 128) * contrast) + 128;

      // Exposure (-100 to 100)
      const exposure = Math.pow(2, settings.exposure / 50);
      r *= exposure;
      g *= exposure;
      b *= exposure;

      // Saturation (-100 to 100)
      const saturation = (settings.saturation / 100) + 1;
      const gray = 0.2989 * r + 0.5870 * g + 0.1140 * b;
      r = gray + saturation * (r - gray);
      g = gray + saturation * (g - gray);
      b = gray + saturation * (b - gray);

      // Temperature (-100 to 100) - warm/cool
      if (settings.temperature > 0) {
        r += settings.temperature * 0.5;
        b -= settings.temperature * 0.3;
      } else {
        r += settings.temperature * 0.3;
        b -= settings.temperature * 0.5;
      }

      // Highlights (-100 to 100)
      if (r > 200 || g > 200 || b > 200) {
        const highlightFactor = settings.highlights * 0.5;
        r += highlightFactor;
        g += highlightFactor;
        b += highlightFactor;
      }

      // Shadows (-100 to 100)
      if (r < 55 && g < 55 && b < 55) {
        const shadowFactor = settings.shadows * 0.5;
        r += shadowFactor;
        g += shadowFactor;
        b += shadowFactor;
      }

      // Clamp values
      data[i] = Math.max(0, Math.min(255, r));
      data[i + 1] = Math.max(0, Math.min(255, g));
      data[i + 2] = Math.max(0, Math.min(255, b));
    }

    ctx.putImageData(imageDataObj, 0, 0);

    // Apply sharpness using convolution (unsharp mask simulation)
    if (settings.sharpness > 0) {
      applySharpness(ctx, canvas.width, canvas.height, settings.sharpness);
    }
  }, [settings, originalImage]);

  // Sharpen using unsharp mask technique
  const applySharpness = (ctx, width, height, amount) => {
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    const factor = amount / 100;
    
    // Simple edge enhancement
    const tempData = new Uint8ClampedArray(data);
    
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = (y * width + x) * 4;
        
        for (let c = 0; c < 3; c++) {
          const current = tempData[idx + c];
          const neighbors = (
            tempData[((y - 1) * width + x) * 4 + c] +
            tempData[((y + 1) * width + x) * 4 + c] +
            tempData[(y * width + x - 1) * 4 + c] +
            tempData[(y * width + x + 1) * 4 + c]
          ) / 4;
          
          const diff = current - neighbors;
          data[idx + c] = Math.max(0, Math.min(255, current + diff * factor));
        }
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
  };

  // Update canvas when settings change
  useEffect(() => {
    if (originalImage) {
      applyFilters();
    }
  }, [settings, applyFilters]);

  // Apply preset
  const applyPreset = (presetId) => {
    const preset = presets.find(p => p.id === presetId);
    if (preset) {
      setActivePreset(presetId);
      setSettings(prev => ({
        ...prev,
        ...preset.settings
      }));
    }
  };

  // Reset to original
  const resetToOriginal = () => {
    setSettings({
      brightness: 0,
      contrast: 0,
      saturation: 0,
      sharpness: 0,
      exposure: 0,
      highlights: 0,
      shadows: 0,
      temperature: 0,
      rotation: 0,
      flipH: false,
      flipV: false
    });
    setActivePreset('original');
  };

  // Rotate image
  const rotateImage = (degrees) => {
    setSettings(prev => ({
      ...prev,
      rotation: (prev.rotation + degrees) % 360
    }));
  };

  // Flip image
  const flipImage = (direction) => {
    if (direction === 'horizontal') {
      setSettings(prev => ({ ...prev, flipH: !prev.flipH }));
    } else {
      setSettings(prev => ({ ...prev, flipV: !prev.flipV }));
    }
  };

  // Auto-enhance (smart adjustment)
  const autoEnhance = () => {
    // Analyze image and apply smart adjustments
    if (!originalImage || !canvasRef.current) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = originalImage.width;
    canvas.height = originalImage.height;
    ctx.drawImage(originalImage, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    // Calculate average brightness and contrast
    let totalBrightness = 0;
    let minBrightness = 255;
    let maxBrightness = 0;

    for (let i = 0; i < data.length; i += 4) {
      const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
      totalBrightness += brightness;
      minBrightness = Math.min(minBrightness, brightness);
      maxBrightness = Math.max(maxBrightness, brightness);
    }

    const avgBrightness = totalBrightness / (data.length / 4);
    const contrastRange = maxBrightness - minBrightness;

    // Apply smart adjustments based on analysis
    const newSettings = {
      brightness: avgBrightness < 100 ? 20 : avgBrightness > 180 ? -10 : 5,
      contrast: contrastRange < 150 ? 30 : contrastRange > 220 ? -10 : 15,
      saturation: -20, // Slightly desaturate for document clarity
      sharpness: 35,
      exposure: avgBrightness < 100 ? 15 : 5,
      highlights: 0,
      shadows: avgBrightness < 100 ? 20 : 0,
      temperature: 0
    };

    setSettings(prev => ({
      ...prev,
      ...newSettings
    }));
    setActivePreset('auto');
  };

  // Save edited image
  const handleSave = async () => {
    if (!canvasRef.current) return;
    
    setSaving(true);
    
    try {
      // Convert canvas to blob
      const blob = await new Promise(resolve => {
        canvasRef.current.toBlob(resolve, 'image/jpeg', 0.92);
      });

      if (onSave) {
        await onSave(blob, settings);
      }
    } catch (error) {
      console.error('Error saving image:', error);
      alert('Failed to save image');
    }
    
    setSaving(false);
  };

  // Download edited image
  const downloadImage = () => {
    if (!canvasRef.current) return;
    
    const link = document.createElement('a');
    link.download = `edited_receipt_${Date.now()}.jpg`;
    link.href = canvasRef.current.toDataURL('image/jpeg', 0.92);
    link.click();
  };

  // Slider component
  const Slider = ({ label, value, onChange, min = -100, max = 100, icon }) => (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm text-gray-600 flex items-center gap-1">
          {icon && <span>{icon}</span>}
          {label}
        </span>
        <span className="text-sm font-medium text-gray-800">{value}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
      />
    </div>
  );

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
        <div className="text-center text-white">
          <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p>Loading image editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-900 z-[100] flex flex-col">
      {/* Header */}
      <div className="bg-gray-800 px-4 py-3 flex items-center justify-between border-b border-gray-700">
        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-white transition-colors"
          >
            ✕
          </button>
          <h2 className="text-white font-semibold text-lg">✨ Receipt Editor</h2>
          <span className="text-gray-500 text-sm">Powered by AI</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={downloadImage}
            className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 text-sm flex items-center gap-2"
          >
            ⬇️ Download
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm flex items-center gap-2"
          >
            {saving ? '💾 Saving...' : '✅ Save & Use'}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Canvas Area */}
        <div className="flex-1 bg-gray-900 flex items-center justify-center p-4 relative">
          {/* Before/After Toggle */}
          <button
            onMouseDown={() => setShowComparison(true)}
            onMouseUp={() => setShowComparison(false)}
            onMouseLeave={() => setShowComparison(false)}
            className="absolute top-4 left-4 px-3 py-2 bg-gray-800/80 text-white rounded-lg text-sm hover:bg-gray-700/80 z-10"
          >
            👁️ Hold to see original
          </button>

          {/* Main Canvas */}
          <div className="relative max-w-full max-h-full">
            <canvas
              ref={canvasRef}
              className={`max-w-full max-h-[calc(100vh-200px)] object-contain rounded-lg shadow-2xl ${showComparison ? 'hidden' : ''}`}
            />
            {showComparison && originalImage && (
              <img
                src={originalImage.src}
                alt="Original"
                className="max-w-full max-h-[calc(100vh-200px)] object-contain rounded-lg shadow-2xl"
              />
            )}
          </div>
        </div>

        {/* Controls Panel */}
        <div className="w-80 bg-gray-800 border-l border-gray-700 flex flex-col">
          {/* Tabs */}
          <div className="flex border-b border-gray-700">
            {[
              { id: 'enhance', label: '✨ Enhance', icon: '✨' },
              { id: 'adjust', label: '🎛️ Adjust', icon: '🎛️' },
              { id: 'transform', label: '🔄 Transform', icon: '🔄' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-3 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id 
                    ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-700/50' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {activeTab === 'enhance' && (
              <div>
                {/* Quick Actions */}
                <div className="mb-6">
                  <h3 className="text-white font-medium mb-3">🚀 Quick Actions</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={autoEnhance}
                      className="px-3 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg text-sm font-medium hover:from-purple-700 hover:to-blue-700 flex items-center justify-center gap-1"
                    >
                      🤖 Auto Enhance
                    </button>
                    <button
                      onClick={resetToOriginal}
                      className="px-3 py-2 bg-gray-700 text-white rounded-lg text-sm hover:bg-gray-600 flex items-center justify-center gap-1"
                    >
                      ↩️ Reset
                    </button>
                  </div>
                </div>

                {/* Presets */}
                <div>
                  <h3 className="text-white font-medium mb-3">📷 Presets</h3>
                  <div className="grid grid-cols-4 gap-2">
                    {presets.map(preset => (
                      <button
                        key={preset.id}
                        onClick={() => applyPreset(preset.id)}
                        className={`flex flex-col items-center p-2 rounded-lg transition-all ${
                          activePreset === preset.id 
                            ? 'bg-blue-600 text-white ring-2 ring-blue-400' 
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        <span className="text-xl mb-1">{preset.icon}</span>
                        <span className="text-xs">{preset.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'adjust' && (
              <div className="text-white">
                <h3 className="font-medium mb-4">🎨 Color Adjustments</h3>
                
                <Slider 
                  label="Brightness" 
                  icon="☀️"
                  value={settings.brightness} 
                  onChange={(v) => setSettings(s => ({ ...s, brightness: v }))} 
                />
                <Slider 
                  label="Contrast" 
                  icon="◐"
                  value={settings.contrast} 
                  onChange={(v) => setSettings(s => ({ ...s, contrast: v }))} 
                />
                <Slider 
                  label="Exposure" 
                  icon="💡"
                  value={settings.exposure} 
                  onChange={(v) => setSettings(s => ({ ...s, exposure: v }))} 
                />
                <Slider 
                  label="Saturation" 
                  icon="🎨"
                  value={settings.saturation} 
                  onChange={(v) => setSettings(s => ({ ...s, saturation: v }))} 
                />
                <Slider 
                  label="Temperature" 
                  icon="🌡️"
                  value={settings.temperature} 
                  onChange={(v) => setSettings(s => ({ ...s, temperature: v }))} 
                />
                <Slider 
                  label="Highlights" 
                  icon="🔆"
                  value={settings.highlights} 
                  onChange={(v) => setSettings(s => ({ ...s, highlights: v }))} 
                />
                <Slider 
                  label="Shadows" 
                  icon="🌑"
                  value={settings.shadows} 
                  onChange={(v) => setSettings(s => ({ ...s, shadows: v }))} 
                />
                <Slider 
                  label="Sharpness" 
                  icon="🔪"
                  value={settings.sharpness} 
                  min={0}
                  onChange={(v) => setSettings(s => ({ ...s, sharpness: v }))} 
                />
              </div>
            )}

            {activeTab === 'transform' && (
              <div className="text-white">
                <h3 className="font-medium mb-4">🔄 Transform</h3>
                
                {/* Rotation */}
                <div className="mb-6">
                  <p className="text-sm text-gray-400 mb-2">Rotate</p>
                  <div className="grid grid-cols-4 gap-2">
                    <button
                      onClick={() => rotateImage(-90)}
                      className="p-3 bg-gray-700 rounded-lg hover:bg-gray-600 text-center"
                    >
                      ↺ 90°
                    </button>
                    <button
                      onClick={() => rotateImage(90)}
                      className="p-3 bg-gray-700 rounded-lg hover:bg-gray-600 text-center"
                    >
                      ↻ 90°
                    </button>
                    <button
                      onClick={() => rotateImage(180)}
                      className="p-3 bg-gray-700 rounded-lg hover:bg-gray-600 text-center"
                    >
                      ↻ 180°
                    </button>
                    <button
                      onClick={() => setSettings(s => ({ ...s, rotation: 0 }))}
                      className="p-3 bg-gray-700 rounded-lg hover:bg-gray-600 text-center"
                    >
                      Reset
                    </button>
                  </div>
                </div>

                {/* Flip */}
                <div className="mb-6">
                  <p className="text-sm text-gray-400 mb-2">Flip</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => flipImage('horizontal')}
                      className={`p-3 rounded-lg text-center transition-colors ${
                        settings.flipH ? 'bg-blue-600' : 'bg-gray-700 hover:bg-gray-600'
                      }`}
                    >
                      ↔️ Horizontal
                    </button>
                    <button
                      onClick={() => flipImage('vertical')}
                      className={`p-3 rounded-lg text-center transition-colors ${
                        settings.flipV ? 'bg-blue-600' : 'bg-gray-700 hover:bg-gray-600'
                      }`}
                    >
                      ↕️ Vertical
                    </button>
                  </div>
                </div>

                {/* Current Transform Info */}
                <div className="p-3 bg-gray-700/50 rounded-lg text-sm">
                  <p className="text-gray-400">Current Transform:</p>
                  <p className="text-white mt-1">
                    Rotation: {settings.rotation}° | 
                    Flip H: {settings.flipH ? 'Yes' : 'No'} | 
                    Flip V: {settings.flipV ? 'Yes' : 'No'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer Info */}
          <div className="p-3 border-t border-gray-700 bg-gray-800/50">
            <p className="text-xs text-gray-500 text-center">
              💡 Tip: Use "Receipt" or "Document" preset for best text clarity
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptEditor;
